import React from 'react';
import { Link } from 'react-router-dom';
import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';

/** Shared helper for staged topic specs that still cite consulting roots. */
function ProductTopicPage(props: { title: string; eyebrow: string; subtitle: React.ReactNode; children: React.ReactNode }) {
  return (
    <>
      <ProductDesignPageHeader title={props.title} eyebrow={props.eyebrow} subtitle={props.subtitle} />
      {props.children}
    </>
  );
}


export const PrioritiesAIBrandElementsPage: React.FC = () => (
  <ProductTopicPage
    title="Brand elements · product adaptations"
    eyebrow="Identity · glyphs"
    subtitle="Glyphs that travel into dark shells need contrast-tested neutrals plus optional hairline rims so they survive TV mode and cramped sidebars alike."
  >
    <ProductDesignSection title="Checklist">
      <ul className="list-disc pl-6 text-[var(--text-sec)] text-sm leading-relaxed space-y-2">
        <li>Minimum clear space mirrors grid rhythm (typically 16px multiples).</li>
        <li>Use sage lockups atop --surface plates, never ambiguous gradients behind production controls.</li>
        <li>Export SVG + PNG ladders for facilitation walls that still screenshot into investor decks.</li>
      </ul>
    </ProductDesignSection>
    <LegacyPeek href="/elevation/brand-elements" label="consulting crest + pattern inventory" />
  </ProductTopicPage>
);

/**
 * Superseded by /design-system/components.
 *
 * This page used to carry three bullets of guidance that are now actively
 * wrong — "rounded-2xl shell + blur backdrop" and "focus rings lean indigo
 * lineage" both predate the Square/Mono system, which is square and focuses in
 * mustard. A reachable page teaching the previous system is worse than no page,
 * because it is indistinguishable from current guidance. The route stays (it is
 * linked from elsewhere) and says plainly where the real catalog is.
 */
export const PrioritiesAIComponentLibraryPage: React.FC = () => (
  <ProductTopicPage
    title="Component library"
    eyebrow="Superseded"
    subtitle="This page has moved. The component library is now rendered from the real source, with props tables and a light/dark toggle."
  >
    <ProductDesignSection title="Go to the catalog">
      <p className="text-sm leading-relaxed text-[var(--text-sec)]">
        <Link to="/elevation/design-system/components" className="text-[var(--mustard)] hover:text-[var(--text-pri)]">
          /design-system/components
        </Link>{' '}
        renders every shared component from its real source, organised atoms → molecules →
        organisms, with a full props table and one named example per parameter. Search matches prop
        names, so typing <code className="font-mono text-[0.85em] text-[var(--mustard)]">showHeader</code>{' '}
        finds the table.
      </p>
      <p className="mt-3 text-sm leading-relaxed text-[var(--text-sec)]">
        What used to be here was three bullets of guidance that are now wrong — they described a
        rounded shell with a blur backdrop and indigo focus rings, which predates the Square/Mono
        system. Reachable guidance for a retired system is worse than none, because nothing on the
        page tells you it is retired.
      </p>
      <p className="mt-3 text-sm leading-relaxed text-[var(--text-sec)]">
        See also{' '}
        <Link to="/elevation/priorities-ai-design-language/foundations" className="text-[var(--mustard)] hover:text-[var(--text-pri)]">
          Foundations
        </Link>{' '}
        for the four rules that are easy to break silently.
      </p>
    </ProductDesignSection>
  </ProductTopicPage>
);

export const PrioritiesAIVisualGrammarPage: React.FC = () => (
  <ProductTopicPage
    title="Visual grammar · restraint"
    eyebrow="Layout · cognition"
    subtitle="Prefer explicit rules, restrained glows, progressive disclosure stacks — metaphors originate in facilitation sessions, ornamentation stays optional."
  >
    <ProductDesignSection title="Alignment with diagram grammar">
      <p className="text-sm text-[var(--text-sec)] leading-relaxed">
        For SVG-heavy storytelling, obey the layered disclosure pattern used in semantic diagram canvases — skeleton first, luminous accent only where attention must converge.
      </p>
    </ProductDesignSection>
    <LegacyPeek href="/elevation/visual-design-grammar" label="expanded consulting grammar notes" />
  </ProductTopicPage>
);

export const PrioritiesAIPatternsPage: React.FC = () => (
  <ProductTopicPage
    title="Patterns & backgrounds · product stance"
    eyebrow="Texture · overlays"
    subtitle="Defer hero textures from consulting slides — inside product shells, tinted panels and rule lines outperform bitmap noise except for intentional hero moments."
  >
    <ProductDesignSection title="When texture is warranted">
      <ul className="list-disc pl-6 text-[var(--text-sec)] text-sm leading-relaxed space-y-2">
        <li>Stage / TV mode MAY borrow subtle grain if contrast ratio remains ≥ APCA thresholds.</li>
        <li>Never place aggressive noise under dense tabular numerics.</li>
      </ul>
    </ProductDesignSection>
    <LegacyPeek href="/elevation/patterns-background-elements" label="full texture collage (consulting exports)" />
  </ProductTopicPage>
);



export const PrioritiesAIToneVoicePage: React.FC = () => (
  <ProductTopicPage
    title="Tone & voice · operator clarity"
    eyebrow="Copy · facilitation"
    subtitle="Product messaging speaks to owners aligning evidence — approachable, kinetic, allergic to needless jargon even when ontology is dense."
  >
    <ProductDesignSection title="Heuristics">
      <ul className="list-disc pl-6 text-[var(--text-sec)] text-sm leading-relaxed space-y-2">
        <li>Prefer verbs that describe observable motion (“publish”, “trace”, “anchor”).</li>
        <li>Surface uncertainty honestly — scaffolding beats false certainty.</li>
        <li>Reserve mythic metaphors for intentional narrative beats, never error states.</li>
      </ul>
    </ProductDesignSection>
    <LegacyPeek href="/elevation/tone-voice-system" label="legacy tone ladder" />
  </ProductTopicPage>
);

export const PrioritiesAIPrinciplesPage: React.FC = () => (
  <ProductTopicPage
    title="Design principles checkpoint"
    eyebrow="Quality · facilitation"
    subtitle="Translate consulting dogmas into measurable product criteria — contrast, resilience, facilitator trust."
  >
    <ProductDesignSection title="Non-negotiables for shipping interfaces">
      <ul className="list-disc pl-6 text-[var(--text-sec)] text-sm leading-relaxed space-y-2">
        <li>Favor evidence surfaces over decorative arcs when budget is contested.</li>
        <li>Progressive disclosure must survive keyboard-only facilitation sessions.</li>
        <li>Every accent color earns a semantic rationale tied to cognition, not brand vanity.</li>
      </ul>
    </ProductDesignSection>
    <LegacyPeek href="/elevation/best-practices-design-principles" label="consulting golden-ratio brief" />
  </ProductTopicPage>
);

function LegacyPeek({ href, label }: { href: string; label: string }) {
  return (
    <ProductDesignSection title="Consulting-era reference archive" hint="Authoritative artefacts still live alongside Peak elevation — cite them deliberately when exporting.">
      <Link to={href} className="text-[var(--mustard)] font-mono text-xs uppercase tracking-[0.16em] hover:text-[var(--text-pri)]">
        Open {label} →
      </Link>
    </ProductDesignSection>
  );
}
